import { KLineData } from "klinecharts";
import { readStyle } from "./indicatorUtils";

export const createParabolicSAR = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const step = (values.Step as number) ?? 0.02;
  const maximum = (values.Maximum as number) ?? 0.2;
  const sarS = readStyle(styles, "sar", "#FF9600");

  return {
    name: "parabolic-sar",
    shortName: "SAR",
    calcParams: [step, maximum],
    series: "price" as const,
    figures: [{ key: "sar", title: "SAR: ", type: "circle" }],
    styles: {
      circles: [
        {
          upColor: sarS.color,
          downColor: sarS.color,
          noChangeColor: sarS.color,
          radius: 2,
        },
      ],
    },
    regenerateFigures: () => [{ key: "sar", title: "SAR: ", type: "circle" }],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [currentStep, currentMaximum] = (indicator.calcParams ?? [
        step,
        maximum,
      ]) as [number, number];
      const n = dataList.length;
      const results: Array<{ sar?: number }> = new Array(n);

      if (n < 2) {
        for (let i = 0; i < n; i++) results[i] = {};
        return results;
      }

      let isUptrend = dataList[1]!.close > dataList[0]!.close;
      let sar = isUptrend ? dataList[0]!.low : dataList[0]!.high;
      let extremePoint = isUptrend ? dataList[0]!.high : dataList[0]!.low;
      let acceleration = currentStep;
      // Bar 0 is seed-only: SAR is undefined without a prior bar (IN-16)
      results[0] = {};

      for (let i = 1; i < n; i++) {
        const current = dataList[i]!;
        const previous = dataList[i - 1]!;
        let newSAR = sar + acceleration * (extremePoint - sar);

        if (isUptrend) {
          newSAR = Math.min(newSAR, previous.low);
          if (i > 1) newSAR = Math.min(newSAR, dataList[i - 2]!.low);
          if (current.low <= newSAR) {
            isUptrend = false;
            newSAR = extremePoint;
            extremePoint = current.low;
            acceleration = currentStep;
          } else if (current.high > extremePoint) {
            extremePoint = current.high;
            acceleration = Math.min(acceleration + currentStep, currentMaximum);
          }
        } else {
          newSAR = Math.max(newSAR, previous.high);
          if (i > 1) newSAR = Math.max(newSAR, dataList[i - 2]!.high);
          if (current.high >= newSAR) {
            isUptrend = true;
            newSAR = extremePoint;
            extremePoint = current.high;
            acceleration = currentStep;
          } else if (current.low < extremePoint) {
            extremePoint = current.low;
            acceleration = Math.min(acceleration + currentStep, currentMaximum);
          }
        }

        sar = newSAR;
        results[i] = { sar };
      }

      return results;
    },
  };
};
